import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Paperclip } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const WHY = [
  { title: "Meaningful Business Challenges", body: "Work on commercial problems involving growth, markets, customers, brands, sales and execution." },
  { title: "Cross-Functional Learning", body: "Gain exposure to connected strategy and implementation challenges." },
  { title: "Ownership and Accountability", body: "Contribute ideas, solve problems and take responsibility for your work." },
  { title: "Building Something Together", body: "Help shape the capabilities and culture of a growing professional organisation." },
];

const initial = { name: "", email: "", phone: "", expertise: "", linkedin: "", note: "" };

export default function CareersPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  usePageMeta({
    title: "Careers at BLEZZINGS | Growth Partners",
    description:
      "Join BLEZZINGS — we're building a multidisciplinary team across strategy, market intelligence, sales transformation, brand, marketing and digital commerce.",
    canonical: "https://blezzings.co/careers",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Careers", url: "https://blezzings.co/careers" },
    ],
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.note.trim()) {
      toast.error("Please fill in your name, email, and a short note.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      if (form.phone.trim()) fd.append("phone", form.phone.trim());
      if (form.expertise.trim()) fd.append("expertise", form.expertise.trim());
      if (form.linkedin.trim()) fd.append("linkedin", form.linkedin.trim());
      fd.append("note", form.note.trim());
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/careers`, fd);
      track("careers_application_submitted", { has_cv: !!cv });
      toast.success("Thanks — we've received your application.", {
        description: "We'll reach out if there's a fit.",
      });
      setForm(initial);
      setCv(null);
      e.target.reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page" className="relative">
      <Nav onBook={() => setBookOpen(true)} />

      {/* Hero */}
      <section data-testid="careers-hero" className="pt-32 md:pt-40 pb-12 md:pb-16 bg-[#fafafa] overflow-hidden">
        <div className={`${GRID} grid grid-cols-12 gap-10 items-center`}>
          <div className="col-span-12 lg:col-span-7">
            <SectionBadge label="Careers at BLEZZINGS" />
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
              Help Build the Next Generation of Commercial Growth Consulting.
            </h1>
            <div className="mt-6 max-w-2xl space-y-5 text-zinc-700 leading-relaxed">
              <p>
                BLEZZINGS Growth Partners is building a multidisciplinary team
                across strategy, market intelligence, sales transformation, brand,
                creative, marketing and digital commerce.
              </p>
              <p>
                We are looking for people who combine curiosity, analytical
                thinking, business understanding and a strong sense of ownership.
              </p>
              <p className="text-black font-medium">
                If you enjoy solving real business problems, translating ideas
                into action and learning across industries and disciplines, we
                would like to hear from you.
              </p>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 reveal">
            <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
              <img
                src="/photos/careers-hero.jpg"
                alt="A young BLEZZINGS consulting team collaborating with a senior partner"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why BLEZZINGS */}
      <section data-testid="careers-why" className="py-16 md:py-20 bg-white">
        <div className={GRID}>
          <SectionBadge label="Why BLEZZINGS" />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {WHY.map((w, i) => (
              <div key={w.title} data-testid={`careers-why-${i}`} className="border-t-2 border-black pt-8">
                <div className="overline">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-xl md:text-2xl leading-tight text-black mt-3">
                  {w.title}
                </h3>
                <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section data-testid="careers-form-section" className="py-16 md:py-20 bg-[#F2F2F2]">
        <div className={`${GRID} grid grid-cols-12 gap-10`}>
          <div className="col-span-12 lg:col-span-4">
            <SectionBadge label="Apply" />
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-6 leading-[1.1]">
              Introduce yourself.
            </h2>
            <p className="mt-4 text-zinc-600 leading-relaxed max-w-sm">
              Tell us who you are, what you are good at, and what you would
              like to build. Attach your CV or share a secure profile link.
            </p>
            <div className="mt-8 hidden lg:block aspect-[4/3] max-w-sm overflow-hidden bg-zinc-200">
              <img
                src="/photos/careers-apply.jpg"
                alt="Preparing a career application — notebook, laptop and coffee on a tidy desk"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <form data-testid="careers-form" onSubmit={submit} className="col-span-12 lg:col-span-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name*" htmlFor="cr-name">
                <Input id="cr-name" data-testid="cr-name" value={form.name} onChange={set("name")} required placeholder="Your full name" className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Email*" htmlFor="cr-email">
                <Input id="cr-email" data-testid="cr-email" type="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Phone" htmlFor="cr-phone">
                <Input id="cr-phone" data-testid="cr-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="Area of Expertise" htmlFor="cr-expertise">
                <Input id="cr-expertise" data-testid="cr-expertise" value={form.expertise} onChange={set("expertise")} placeholder="e.g. Strategy, Performance Marketing, Sales" className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="LinkedIn URL" htmlFor="cr-linkedin">
                <Input id="cr-linkedin" data-testid="cr-linkedin" type="url" value={form.linkedin} onChange={set("linkedin")} placeholder="https://www.linkedin.com/in/..." className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black h-11" />
              </Field>
              <Field label="CV Upload (PDF / Word, optional)" htmlFor="cr-cv">
                <label
                  htmlFor="cr-cv"
                  data-testid="cr-cv-label"
                  className="flex items-center gap-3 h-11 px-3 border border-zinc-300 bg-white cursor-pointer text-sm text-zinc-600 hover:border-black transition-colors"
                >
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="truncate">{cv ? cv.name : "Attach CV — or share a secure profile link above"}</span>
                </label>
                <input
                  id="cr-cv"
                  data-testid="cr-cv"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="sr-only"
                  onChange={(e) => setCv(e.target.files?.[0] || null)}
                />
              </Field>
            </div>

            <Field label="Short Note*" htmlFor="cr-note">
              <Textarea id="cr-note" data-testid="cr-note" value={form.note} onChange={set("note")} required rows={4} placeholder="A short note about your background, experience, and what you'd like to build with us." className="rounded-none border-zinc-300 bg-white focus-visible:ring-0 focus-visible:border-black resize-none" />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <div className="space-y-2 max-w-xs">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  By submitting, you agree to your information (including your
                  CV) being processed in accordance with our{" "}
                  <Link to="/privacy-policy" data-testid="cr-privacy-link" className="underline underline-offset-2 hover:text-zinc-800 transition-colors">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-of-use" data-testid="cr-terms-link" className="underline underline-offset-2 hover:text-zinc-800 transition-colors">
                    Terms of Use
                  </Link>
                  .
                </p>
                <p className="text-xs text-zinc-500">We reply within one business week.</p>
              </div>
              <button
                data-testid="cr-submit"
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center justify-center gap-2 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Sending</>
                ) : (
                  <>
                    Submit Application
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => setBookOpen(true)} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
